import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  ApiService,
  AppointmentModel,
  AUTHORIZATION_STATUS,
  AuthorizationModel,
  ClinicService,
  COLOR, CrudService, eligibilityStatusColor,
  getAuthorizationStatusColor,
  getAuthorizationStatusText,
  getWorstAuthorizationStatusText,
  Patient,
  PatientInsurance,
  PatientInsuranceType,
  Permission,
  PermissionsService,
  ProgressIndicatorService,
  sortPatientInsurance,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatestWith, filter, finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs'

import { AuthorizationDialogComponent, AuthorizationDialogData } from '../../authorization-dialog'

interface StatusState {
  tooltipText: string
  insuranceItems: {
    insurance: PatientInsurance
    status?: AUTHORIZATION_STATUS
    statusColor?: COLOR
  }[]
  statuses: {
    primary?: COLOR
    secondary?: COLOR
  }
}

@Component({
  selector: 'mmx-authorization-status-icon',
  templateUrl: 'authorization-status-icon.component.html',
  standalone: false,
})
export class AuthorizationStatusIconComponent implements OnInit, OnChanges, OnDestroy {
  private onDestroy$ = new Subject<void>()
  public statusChanged = new EventEmitter<void>()

  @Input() patient?: Patient
  @Input() appointment?: AppointmentModel
  @Input() statuses?: AUTHORIZATION_STATUS[]
  @Input() workflow = false
  @Output() authorizationSaved = new EventEmitter<void>()

  public isLoadingAuths = new BehaviorSubject(false)
  public inProgress = false
  public state$ = new BehaviorSubject<StatusState>(this.getUpdatedState())
  public isAuthorizationRuleRequired$ =
    this.permissionsService.hasPermission('auth.manager')
  public readonly Permission = Permission
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS
  public readonly getAuthorizationStatusColor = getAuthorizationStatusColor
  public readonly getAuthorizationStatusText = getAuthorizationStatusText
  private tz: string

  private patientInsurances: PatientInsurance[]

  public fetchAuths$ = new Subject()

  public appointmentAuths = this.fetchAuths$.pipe(switchMap(() => {
    if (this.appointment?.patientId) {
      this.isLoadingAuths.next(true)
      return this.crudService.index(AuthorizationModel, { patientId: this.appointment.patientId })
        .pipe(combineLatestWith(
          this.patientInsurances ?
            of(this.patientInsurances) :
            this.crudService.index(PatientInsurance, { patientId: this.appointment.patientId }),
        ),
        map(([authorizations, patientInsurances]) => {
          this.patientInsurances = patientInsurances
          return authorizations?.filter(authorization => authorization.appointmentIds.has(this.appointment.id)) || []
        }), finalize(() => {
          this.isLoadingAuths.next(false)
        }))
    }

    return of([])
  }))

  constructor(
  private crudService: CrudService,
    private clinicService: ClinicService,
    private snackService: ProgressIndicatorService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
    private restService: ApiService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.tz = this.clinicService.clinic.settings.defaultTimezone ?? 'America/New_York'
  }

  fetchAuths(event) {
    event.stopPropagation()
    this.fetchAuths$.next(null)
  }

  public ngOnChanges() {
    this.update()
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public openConfirmDialog(authorization) {
    (this.patient ? of(this.patient) : this.restService.getPatient(this.appointment.patientId, true))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((patient) => {
        const data: AuthorizationDialogData = {
          patient,
          patientInsurances: this.patientInsurances,
          appointment: this.appointment,
          authorization,
          timezone: this.tz,
        }
        this.dialog.open(AuthorizationDialogComponent, {
          width: '800px',
          data,
          panelClass: 'dialog-form',
          autoFocus: false,
        })
          .afterClosed()
          .pipe(
            takeUntil(this.onDestroy$),
            filter(Boolean),
          )
          .subscribe({
            next: () => {
              this.authorizationSaved.emit()
              this.snackService.snack({
                message: this.translate.instant('icons.authorizations.updated'),
                duration: 5000,
              })
            },
            error: () => {
              this.snackService.snack({
                message: this.translate.instant('icons.authorizations.error'),
                duration: 5000,
              })
            },
          })
      })
  }

  private update(): void {
    this.state$.next(this.getUpdatedState())
  }

  private getUpdatedState(): StatusState {
    const authorizationStatuses = (this.appointment?.authorizations || []).map(
      (r) => r.status,
    )
    const insuranceItems = this.getInsuranceItems()

    const state: StatusState = {
      insuranceItems,
      statuses: {
        primary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.PRIMARY,
        )?.statusColor,
        secondary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.SECONDARY,
        )?.statusColor,
      },
      tooltipText: getWorstAuthorizationStatusText(authorizationStatuses),
    }

    return state
  }

  private getInsuranceItems() {
    const insurances = sortPatientInsurance(this.appointment?.insurances || [])
    return insurances.map((insurance) => {
      const auth = (this.appointment.authorizations || []).find(
        ({ insuranceId }) => insuranceId === insurance.id,
      )
      if (!auth) {
        return { insurance }
      }

      return {
        insurance,
        status: auth.status,
        statusColor: getAuthorizationStatusColor(auth.status),
      }
    })
  }

  getInsurance(carrierLookupId: string) {
    return this.patientInsurances.find(insurance => insurance.carrierLookupId === carrierLookupId)
  }

  protected readonly eligibilityStatusColor = eligibilityStatusColor
}
